import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const Register = () => {
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  
  // OTP and stage management
  const [stage, setStage] = useState('DETAILS'); // DETAILS or OTP
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [lastOtp, setLastOtp] = useState(null); // Store the last OTP received
  const navigate = useNavigate();

  // Timer useEffect - runs every second when timer is active
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (timer === 0 && stage === 'OTP') {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer, stage]);

  // Auto-fill OTP if available
  useEffect(() => {
    if (lastOtp && stage === 'OTP') {
      setOtp(lastOtp.toString());
    }
  }, [lastOtp, stage]);

  const validateForm = () => {
    const { name, email, phone, password, confirmPassword } = formData;

    if (!name || !email || !phone || !password || !confirmPassword) {
      return 'All fields are required.';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid email format.';
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(phone)) {
      return 'Phone number must be exactly 10 digits.';
    }

    if (password.length < 6) {
      return 'Password must be at least 6 characters.';
    }

    if (password !== confirmPassword) {
      return 'Passwords do not match.';
    }

    return '';
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleOtpChange = (e) => {
    // Allow only numbers and limit to 6 digits
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setMessage('');

    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setLoading(true);
    try {
      // Don't send confirmPassword to the API
      const { confirmPassword, ...dataToSend } = formData;
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/sendOtp`,
        dataToSend
      );
      
      // Store the OTP from response for display
      if (response.data.otp) {
        setLastOtp(response.data.otp);
        toast.info('OTP sent to your phone! Also displayed below due to SMS service issues.');
      } else {
        toast.info('OTP sent to your phone!');
      }
      
      setStage('OTP');
      setTimer(60); // Set timer for 60 seconds
      setCanResend(false);
      setOtp('');
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;

    setLoading(true);
    try {
      // Don't send confirmPassword to the API
      const { confirmPassword, ...dataToSend } = formData;
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/sendOtp`,
        dataToSend
      );
      
      // Store the OTP from response for display
      if (response.data.otp) {
        setLastOtp(response.data.otp);
        toast.info('OTP resent! Also displayed below due to SMS service issues.');
      } else {
        toast.info('OTP resent!');
      }
      
      setTimer(60); // Reset timer to 60 seconds
      setCanResend(false);
      setOtp('');
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      setMessage('OTP must be 6 digits');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        phone: formData.phone,
        otp: otp
      };
      
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/verifyOtpAndRegister`,
        payload
      );
      
      toast.success('Registration successful!');
      // Reset form and go back to details stage
      setStage('DETAILS');
      setFormData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
      setOtp('');
      setTimer(0);
      setCanResend(false);
      setLastOtp(null);
      navigate("/login");
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  // Format timer to display as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
            {stage === 'DETAILS' ? 'Create Account' : 'Verify OTP'}
          </h2>

          {stage === 'DETAILS' ? (
            <form onSubmit={handleSendOtp}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength="10"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="10-digit mobile number"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="At least 6 characters"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm your password"
                    required
                  />
                </div>

                {message && (
                  <div className="text-red-500 text-sm text-center py-2">
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition duration-300 disabled:opacity-50"
                >
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <div className="space-y-6">
                <div className="text-center">
                  <p className="text-gray-600">
                    OTP sent to <span className="font-semibold">{formData.phone}</span>
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Enter the 6-digit verification code
                  </p>
                  
                  {/* Display OTP due to SMS service issues */}
                  {lastOtp && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 mb-1">
                        SMS service is having issues. Your OTP is:
                      </p>
                      <div className="text-center">
                        <span className="text-2xl font-bold text-yellow-700">{lastOtp}</span>
                      </div>
                      <p className="text-xs text-yellow-600 mt-1">
                        This OTP has been auto-filled for your convenience.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={handleOtpChange}
                    className="w-full px-4 py-3 text-center text-xl border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="------"
                    maxLength={6}
                    inputMode="numeric"
                    autoFocus
                  />
                </div>

                <div className="flex justify-between items-center">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={!canResend || loading}
                    className={`text-sm ${canResend ? 'text-blue-600 hover:underline font-medium' : 'text-gray-500'}`}
                  >
                    {canResend ? 'Resend OTP' : `Resend OTP in ${formatTime(timer)}`}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setStage('DETAILS');
                      setMessage('');
                      setTimer(0);
                      setCanResend(false);
                      setLastOtp(null);
                    }}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Change Number
                  </button>
                </div>

                {message && (
                  <div className="text-red-500 text-sm text-center py-2">
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition duration-300 disabled:opacity-50"
                >
                  {loading ? 'Verifying...' : 'Register'}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 font-medium hover:underline">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;